import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  MessageSquareWarning,
  PhoneCall,
  Settings,
  Users,
} from 'lucide-react';
import { isAdminRole, useAuthStore } from '@/lib/auth-store';
import { logoutSession } from '@/lib/api';
import { displayVersion } from '@/lib/release-metadata';
// Issue #175：管理后台品牌使用透明背景 jzlogo（与移动端同源），暗色侧栏无白底。
import jzLogo from '@/assets/jzlogo.png';
import clsx from 'clsx';

/**
 * 管理后台侧边栏入口（Issue #160 设计稿 UI_B_Detail_05_AdminDashboard）。
 * 导出以便结构守卫测试（admin-nav.spec.ts）按数据断言，避免漂移。
 *
 * Issue #328：移除「质检报告」入口——它与「通话记录」展示内容完全相同，造成重复。
 * `/reports` 路由保留（见 App.tsx）以兼容历史链接，仅侧栏不再展示该入口。
 */
export const NAV_ITEMS = [
  { to: '/overview', label: '数据总览', icon: LayoutDashboard },
  { to: '/calls', label: '通话记录', icon: PhoneCall },
  { to: '/complaints', label: '投诉处理', icon: ClipboardCheck },
  { to: '/employees', label: '员工管理', icon: Users },
  { to: '/knowledge-base', label: '知识库', icon: BookOpen },
  // Issue #341：题目纠错 / 优化建议处理台。
  { to: '/corrections', label: '题目纠错', icon: MessageSquareWarning },
  { to: '/settings', label: '系统设置', icon: Settings },
] as const;

/** 非管理员处理人只获得投诉工作台入口；其它后台模块仍由路由守卫二次拒绝。 */
export const COMPLAINT_HANDLER_NAV_ITEMS = [
  { to: '/complaints', label: '投诉处理', icon: ClipboardCheck },
] as const;

export function AdminLayout() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const admin = isAdminRole(user?.role);
  const navItems = admin ? NAV_ITEMS : COMPLAINT_HANDLER_NAV_ITEMS;

  return (
    <div className="flex min-h-screen bg-slate-100 text-slate-900">
      <aside
        className="flex w-60 flex-col justify-between bg-gradient-to-b from-sidebar-900 via-sidebar-800 to-sidebar-700 text-slate-100"
        data-testid="admin-sidebar"
      >
        <div>
          <div className="flex items-center gap-3 px-6 py-6">
            <img src={jzLogo} alt="金专商学院 Logo" className="h-10 w-10 object-contain" />
            <div>
              <div className="text-base font-semibold tracking-wide">金专商学院</div>
              <div className="text-xs text-slate-400">
                {admin ? '管理后台' : '受限投诉处理台'}
              </div>
            </div>
          </div>
          <nav className="mt-2 flex flex-col gap-1 px-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  clsx(
                    'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-accent-500/15 text-accent-200 shadow-inner shadow-accent-500/10'
                      : 'text-slate-300 hover:bg-white/5 hover:text-white',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive ? (
                      <span className="absolute inset-y-1 left-0 w-1 rounded-r-full bg-gradient-to-b from-accent-300 to-accent-500" />
                    ) : null}
                    <item.icon
                      className={clsx(
                        'h-4 w-4',
                        isActive ? 'text-accent-300' : 'text-slate-400 group-hover:text-white',
                      )}
                    />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="px-4 pb-6">
          <div className="rounded-xl bg-gradient-to-br from-sidebar-700 to-sidebar-600 px-3 py-3 text-xs text-slate-200 ring-1 ring-white/5">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-accent-400 to-accent-600 text-sm font-semibold text-white">
                {(user?.name ?? '管').slice(0, 1)}
              </div>
              <div>
                <div className="text-sm font-semibold text-white">
                  {user?.name ?? '管理员'}
                </div>
                <div className="text-[11px] text-slate-300">
                  {user?.deptName ?? '系统管理'}
                </div>
              </div>
            </div>
            <div className="mt-2 inline-flex rounded-md bg-accent-500/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-accent-200 ring-1 ring-accent-400/40">
              {user?.role ?? 'ADMIN'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              // Issue #401：真实后端登出——撤销并清除 HttpOnly refresh cookie，
              // 再清内存会话；后端不可达时也完成本地登出（不阻塞跳转）。
              void logoutSession().finally(() =>
                navigate('/login', { replace: true }),
              );
            }}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 transition-colors hover:bg-white/10"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> 退出登录
          </button>
          <div
            className="mt-3 text-center text-[10px] tracking-wider text-slate-400"
            data-testid="admin-sidebar-version"
          >
            {displayVersion()}
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1440px] px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
